import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { List } from 'immutable';
import { Popover, PopoverPanel } from '@headlessui/react';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import useClickOutsideHandler from 'flow/hooks/useClickOutsideHandler';
import { Extension } from 'flow/types/App';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { extractName } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import ActiveProfileStatus from 'flow/components/ui-components/ActiveProfileStatus';

// The state kept by the mention inserter.
export type MentionInserterState =
    | {
          open: false;
      }
    | {
          open: true;
          // Words after the @ that we use to filter matches
          filterWords: string[];
          // The region we replace if inserting a mention
          replaceFrom: number;
          replaceTo: number;
          // The current proposed insertion, if any.
          proposedInsertion?: string;
          // If we're using arrow navigation, then the offset of the item to insert (set on
          // key move, validated and the proposed insertion sync'd by the component).
          insertionOffset?: number;
      };

// Initial mention inserter state.
export const getInitialMentionInserterState = (): MentionInserterState => ({
    open: false,
});

// Function to calculate the mention inserter state based upon the message
// input text area.
export const getNextMentionInserterState = (
    currentState: MentionInserterState,
    input: HTMLTextAreaElement,
): MentionInserterState => {
    // Look for an @ behind the cursor
    const message = input.value;
    const cursorPosition = input.selectionStart;
    const precedingAt = message.lastIndexOf('@', cursorPosition);
    if (precedingAt < 0) {
        // No @ behind the cursor, so can't possibly be completing a mention.
        return { open: false };
    }

    // Gather words between the @ and the cursor.
    const words = message.slice(precedingAt + 1, cursorPosition).split(/\s+/);
    if (words.length > 2) {
        // More than a first name and last name, so not a good candidate for completion.
        return { open: false };
    }

    // Candidate for the mention inserter.
    return {
        ...currentState,
        open: true,
        filterWords: words,
        replaceFrom: precedingAt,
        replaceTo: cursorPosition,
    };
};

// This is called when there is a keystroke in the message input, and gives the mention inserter
// chance to process or act upon it. Returns true if there should be no further processing.
export const offerKeyEventToMessageInserter = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    state: MentionInserterState,
    updateState: (update: MentionInserterState) => void,
    message: string,
    updateMessage: (update: string, cursorPosition: number) => void,
) => {
    // If it's not open or there's no proposed insertion, nothing to do.
    if (!state.open || !state.proposedInsertion) {
        return false;
    }

    // Otherwise, go by key.
    switch (event.key) {
        case 'Enter': {
            // Insert the proposed mention and close; don't propagate the event.
            insertMention(state, updateState, message, updateMessage);
            event.preventDefault();
            return true;
        }
        case 'Escape': {
            // Close the mention inserter.
            updateState({ open: false });
            return true;
        }
        case 'ArrowUp': {
            updateState({
                ...state,
                insertionOffset: state.insertionOffset - 1,
            });
            event.preventDefault();
            return true;
        }
        case 'ArrowDown': {
            updateState({
                ...state,
                insertionOffset: state.insertionOffset + 1,
            });
            event.preventDefault();
            return true;
        }
    }
    return false;
};

const insertMention = (
    state: MentionInserterState,
    updateState: (update: MentionInserterState) => void,
    message: string,
    updateMessage: (update: string, cursorPosition: number) => void,
) => {
    if (!state.open) {
        return;
    }
    const before = message.substring(0, state.replaceFrom);
    const toInsert = '@' + state.proposedInsertion + ' ';
    updateMessage(
        before + toInsert + message.substring(state.replaceTo),
        before.length + toInsert.length,
    );
    updateState({ open: false });
};

interface Props {
    state: MentionInserterState;
    updateState: (update: MentionInserterState) => void;
    message: string;
    updateMessage: (update: string, cursorPosition: number) => void;
    sessionKey: ChatSessionKey;
}

// Component is a popover showing the mentions we could potentially insert.
export const MentionInserter = ({
    state,
    updateState,
    message,
    updateMessage,
    sessionKey,
}: Props) => {
    // Set of all mentionables are those in the current chat.
    const mentionables = useAppSelector((state) => {
        const allExtensions = state.extensions.get('extensions');
        if (!allExtensions) {
            return undefined;
        }
        return state.chats
            .getIn(['sessions', sessionKey, 'members'])
            ?.map((m) => allExtensions.get(m.get('extensionKey')));
    });

    // Decide what mentionables are applicable.
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const filteredMentionables = useMemo(
        () =>
            mentionables
                ?.filter((mentionable) => {
                    // If we're not open, no mentionables make it through.
                    if (!state.open) {
                        return false;
                    }

                    // If the mentionable is us, or lacks contact information, it's never
                    // applicable.
                    if (
                        !mentionable ||
                        mentionable.get('key') === ownExtensionKey
                    ) {
                        return false;
                    }
                    const name: string = extractName(
                        mentionable.get('contact'),
                        '',
                    );
                    if (!name) {
                        return false;
                    }

                    // Filter, by both names if two words, or either name if one word.
                    const nameParts = name.split(' ');
                    switch (state.filterWords.length) {
                        case 1:
                            return nameParts.some((p) =>
                                p
                                    .toLowerCase()
                                    .startsWith(
                                        state.filterWords[0].toLowerCase(),
                                    ),
                            );
                        case 2:
                            return (
                                (nameParts[0] || '')
                                    .toLowerCase()
                                    .startsWith(
                                        state.filterWords[0].toLowerCase(),
                                    ) &&
                                (nameParts[1] || '')
                                    .toLowerCase()
                                    .startsWith(
                                        state.filterWords[1].toLowerCase(),
                                    )
                            );
                        default:
                            return true;
                    }
                })
                ?.sort(sortByName),
        [
            mentionables,
            state.open,
            state.open ? state.filterWords.join(' ') : '',
        ],
    );

    // The displayed mentions (accounting for arrow key handling), as well as the current offset
    // of the mentions we show (to give nicer UX when we use arrow keys by minimizing the amount
    // of scrolling we do).
    const [displayedMentionables, setDisplayedMentionables] =
        useState<List<TypedMap<Extension>>>();
    const [offsetStart, setOffsetStart] = useState(0);
    const maxMentions = 5;
    useEffect(() => {
        // If no mentions to show, don't go any further, and make sure we're in closed state.
        if (
            !state.open ||
            !filteredMentionables ||
            filteredMentionables.size === 0
        ) {
            setDisplayedMentionables(undefined);
            setOffsetStart(0);
            updateState({ open: false });
            return;
        }

        // Make sure insertion offset is in range; reset to 0 if not (or if unset), and determine
        // correct insertion candidate. Update state if needed.
        const newInsertionOffset = Math.min(
            Math.max(state.insertionOffset || 0, 0),
            filteredMentionables.size - 1,
        );
        const newProposedInsertion = extractName(
            filteredMentionables.getIn([newInsertionOffset, 'contact']),
            '',
        );
        if (
            state.insertionOffset !== newInsertionOffset ||
            state.proposedInsertion !== newProposedInsertion
        ) {
            updateState({
                ...state,
                insertionOffset: newInsertionOffset,
                proposedInsertion: newProposedInsertion,
            });
        }

        // Make sure the offset start doesn't result in us showing less than the
        // desired number of mentions, and that the mention we are currently on
        // is in the window.
        let newOffsetStart = offsetStart;
        if (filteredMentionables.size - offsetStart < maxMentions) {
            newOffsetStart = 0;
        }
        if (newInsertionOffset < offsetStart) {
            // We're too far down the list; move up.
            newOffsetStart = newInsertionOffset;
        } else if (newInsertionOffset >= offsetStart + maxMentions) {
            // We're too far up the list; move down.
            newOffsetStart = newInsertionOffset - maxMentions + 1;
        }
        setDisplayedMentionables(
            filteredMentionables.slice(
                newOffsetStart,
                newOffsetStart + maxMentions,
            ),
        );
        if (newOffsetStart !== offsetStart) {
            setOffsetStart(newOffsetStart);
        }
    }, [
        filteredMentionables?.size || 0,
        state.open ? state.insertionOffset : -1,
        offsetStart,
    ]);

    const { i18next } = useAppContext();
    const statuses = useAppSelector((state) =>
        state.extensions.get('statuses'),
    );
    const popoverRef = useClickOutsideHandler(() =>
        updateState({ open: false }),
    );
    return (
        <Popover>
            {state.open && displayedMentionables && (
                <PopoverPanel
                    static={true}
                    className="absolute top-0 z-40 w-[360px] -translate-y-full rounded-sm bg-white p-2 shadow-lg outline-hidden"
                    ref={popoverRef}
                >
                    <div className="flex flex-col">
                        {displayedMentionables.map((mentionable, index) => {
                            const contact = mentionable.get('contact');
                            const name = extractName(
                                contact,
                                i18next.t('Contact.Unknown'),
                            );
                            const active =
                                state.open && name === state.proposedInsertion;
                            return (
                                <div
                                    key={mentionable.get('chatUserKey')}
                                    className={twMerge(
                                        'group flex h-12 w-full cursor-pointer items-center gap-x-4 rounded-sm p-2',
                                        active ? 'bg-gray-200' : '',
                                    )}
                                    onMouseEnter={() =>
                                        updateState({
                                            ...state,
                                            insertionOffset:
                                                offsetStart + index,
                                        })
                                    }
                                    onWheel={(e) => {
                                        const notches =
                                            Math.ceil(
                                                Math.abs(e.deltaY / 120),
                                            ) * (e.deltaY < 0 ? -1 : 1);
                                        const maxOffsetStart = Math.max(
                                            filteredMentionables.size -
                                                maxMentions,
                                            0,
                                        );
                                        setOffsetStart(
                                            Math.min(
                                                Math.max(
                                                    offsetStart + notches,
                                                    0,
                                                ),
                                                maxOffsetStart,
                                            ),
                                        );
                                    }}
                                    onClick={() =>
                                        insertMention(
                                            {
                                                ...state,
                                                proposedInsertion: name,
                                            },
                                            updateState,
                                            message,
                                            updateMessage,
                                        )
                                    }
                                >
                                    <ContactAvatar
                                        contact={contact}
                                        size="small"
                                        status={
                                            statuses.get(
                                                mentionable.get('key'),
                                            ) || 'unknown'
                                        }
                                    />
                                    <div className="flex w-[300px] flex-col">
                                        <p className="w-full truncate overflow-hidden text-base/4 whitespace-nowrap">
                                            {name}
                                        </p>
                                        {active && (
                                            <p className="w-full truncate overflow-hidden text-sm/4 whitespace-nowrap text-gray-500">
                                                <ActiveProfileStatus
                                                    extensionKey={mentionable?.get(
                                                        'key',
                                                    )}
                                                />
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </PopoverPanel>
            )}
        </Popover>
    );
};

const sortByName = (a: TypedMap<Extension>, b: TypedMap<Extension>) => {
    const aName = extractName(a.get('contact'));
    const bName = extractName(b.get('contact'));
    return aName < bName ? -1 : 1;
};
